import React from "react";
import { Box, Button, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { ArrowRight } from "lucide-react";
import { EnrolledGroupItem } from "../types";
import { formatEnrollTypeLabel } from "../config/enrollTypeMap";

interface EnrolledGroupRowProps {
  group: EnrolledGroupItem;
  onGoToGroup?: (group: EnrolledGroupItem) => void;
}

const NeutralBadge: React.FC<{ label: string }> = ({ label }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        padding: "4px 10px",
        borderRadius: "8px",
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        fontSize: "12px",
        fontWeight: 600,
        color: theme.palette.text.primary,
        lineHeight: 1.2,
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </Box>
  );
};

const EnrolledGroupRow: React.FC<EnrolledGroupRowProps> = ({
  group,
  onGoToGroup,
}) => {
  const theme = useTheme();
  const { t } = useTranslation("users-and-groups");
  const typeLabel = group.groupType === "DYNAMIC" ? "Dinamico" : "Statico";
  const enrollLabel = formatEnrollTypeLabel(group.enrollmentType);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        padding: "14px 16px",
        borderRadius: "10px",
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        [theme.breakpoints.down("lg")]: {
          flexDirection: "column",
          alignItems: "stretch",
          gap: 1.5,
        },
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: "14px",
            fontWeight: 700,
            color: theme.palette.text.primary,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {group.groupName || `Gruppo ${group.id}`}
        </Typography>
        <Typography
          sx={{
            fontSize: "12px",
            color: theme.palette.text.primary,
            lineHeight: 1.4,
          }}
        >
          {`ID: ${group.id} | ${typeLabel}`}
        </Typography>
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          flexShrink: 0,
          [theme.breakpoints.down("lg")]: {
            justifyContent: "flex-start",
            flexWrap: "wrap",
          },
        }}
      >
        {enrollLabel && <NeutralBadge label={enrollLabel} />}

        {onGoToGroup && (
          <Button
            variant="text"
            endIcon={<ArrowRight size={16} />}
            onClick={() => onGoToGroup(group)}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              color: theme.palette.primary.main,
            }}
          >
            {t("row.go-to-group")}
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default EnrolledGroupRow;
